import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const MemberCoordinator = () => {
  const [memberApplications, setMemberApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState(null);

  useEffect(() => {
    const fetchMemberApplications = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'memberApplications'));
        const applications = [];
        querySnapshot.forEach((doc) => {
          applications.push({ id: doc.id, ...doc.data() });
        });
        setMemberApplications(applications);
        setFilteredApplications(applications);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching member applications:', error);
        setLoading(false);
      }
    };

    fetchMemberApplications();
  }, []);

  useEffect(() => {
    let filtered = memberApplications;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        (app.fullNames && app.fullNames.toLowerCase().includes(term)) ||
        (app.personalEmailAddress && app.personalEmailAddress.toLowerCase().includes(term)) ||
        (app.contactNumbersCallsMain && app.contactNumbersCallsMain.includes(term))
      );
    }
    
    setFilteredApplications(filtered);
  }, [statusFilter, searchTerm, memberApplications]);

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      const docRef = doc(db, 'memberApplications', applicationId);
      await updateDoc(docRef, {
        status,
        coordinatorReviewedAt: new Date(),
        coordinatorId: 'current_user_id' // Replace with actual user ID
      });

      // Update local state
      setMemberApplications(prev => 
        prev.map(application => 
          application.id === applicationId ? { ...application, status } : application
        )
      );
      
      alert(`Application status updated to ${status}`);
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Error updating application status');
    }
  };

  const handleRespondToQuery = async () => {
    if (!selectedApplication) return;

    try {
      const appRef = doc(db, 'memberApplications', selectedApplication.id);
      await updateDoc(appRef, {
        coordinatorResponse: responseMessage,
        responseTimestamp: new Date()
      });
      alert('Response sent successfully!');
      setResponseMessage('');
      setSelectedApplication(null);

      // Refresh applications
      const querySnapshot = await getDocs(collection(db, 'memberApplications'));
      const applications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMemberApplications(applications);
      setFilteredApplications(applications);
    } catch (error) {
      console.error('Error responding to query:', error);
      alert('Error sending response. Please try again.');
    }
  };

  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setEditFormData({ ...application });
    setResponseMessage(application.coordinatorQuery || '');
    setShowModal(true);
    setEditMode(false);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setEditFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name.includes('children') || name.includes('parentsInLaws') || name.includes('extendedFamily')) {
      const [field, index, subField] = name.split('.');
      setEditFormData(prev => {
        const newArray = [...prev[field]];
        newArray[index] = { ...newArray[index], [subField]: value };
        return { ...prev, [field]: newArray };
      });
    } else if (name.includes('.')) {
      const [arrayField, subField] = name.split('.');
      setEditFormData(prev => ({
        ...prev,
        [arrayField]: { ...prev[arrayField], [subField]: value }
      }));
    } else {
      setEditFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const saveEditedForm = async () => {
    if (!selectedApplication) return;

    try {
      const docRef = doc(db, 'memberApplications', selectedApplication.id);
      await updateDoc(docRef, {
        ...editFormData,
        coordinatorEditedAt: new Date(),
        coordinatorId: 'current_user_id' // Replace with actual user ID
      });

      // Update local state
      setMemberApplications(prev =>
        prev.map(application =>
          application.id === selectedApplication.id ? { ...application, ...editFormData } : application
        )
      );
      setFilteredApplications(prev =>
        prev.map(application =>
          application.id === selectedApplication.id ? { ...application, ...editFormData } : application
        )
      );

      alert('Form updated successfully!');
      setEditMode(false);
      setSelectedApplication({ ...selectedApplication, ...editFormData });
    } catch (error) {
      console.error('Error updating form:', error);
      alert('Error updating form. Please try again.');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
    setEditFormData(null);
    setResponseMessage('');
    setEditMode(false);
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Contact', 'Status', 'Submitted Date'];
    const csvData = filteredApplications.map(app => [
      app.fullNames || 'N/A',
      app.personalEmailAddress || 'N/A',
      app.contactNumbersCallsMain || 'N/A',
      app.status || 'pending',
      app.submittedAt ? new Date(app.submittedAt.seconds * 1000).toLocaleDateString() : 'N/A'
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'member-applications.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading member applications...</div>;
  }

  return (
    <div className="dashboard">
      <h2>Member Coordinator Dashboard</h2>
      
      <div className="dashboard-controls">
        <div className="filter-control">
          <label>Filter by Status:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Applications</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        
        <div className="search-control">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <button onClick={exportToCSV} className="btn-primary">
          Export to CSV
        </button>
      </div>

      <div className="dashboard-section">
        <h3>Member Applications ({filteredApplications.length})</h3>
        <div className="table-container">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Submitted Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map(application => (
                <tr key={application.id}>
                  <td>{application.fullNames || 'N/A'} {application.coordinatorQuery && '(Query)'}</td>
                  <td>{application.personalEmailAddress || 'N/A'}</td>
                  <td>{application.contactNumbersCallsMain || 'N/A'}</td>
                  <td>{application.submittedAt ? new Date(application.submittedAt.seconds * 1000).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${application.status || 'pending'}`}>
                      {application.status || 'Pending'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-primary"
                        onClick={() => viewApplicationDetails(application)}
                      >
                        View
                      </button>
                      <select 
                        value={application.status || 'pending'} 
                        onChange={(e) => updateApplicationStatus(application.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="under_review">Under Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredApplications.length === 0 && (
            <p className="no-data">No member applications found.</p>
          )}
        </div>
      </div>

      {/* Application Detail Modal */}
      {showModal && selectedApplication && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Member Application Details: {selectedApplication.fullNames || 'N/A'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="application-details">
                {editMode ? (
                  <div>
                    <h4>FMLC Representative - Edit Application</h4>
                    <h5>Application Type</h5>
                    <div className="form-group">
                      <label>Application Type:</label>
                      <select
                        name="applicationType"
                        value={editFormData.applicationType || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select Type</option>
                        <option value="new">New</option>
                        <option value="renewal">Renewal</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Membership Plan:</label>
                      <select
                        name="membershipPlan"
                        value={editFormData.membershipPlan || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select Plan</option>
                        <option value="standard">Standard</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          name="memberCardFee"
                          checked={editFormData.memberCardFee || false}
                          onChange={handleEditChange}
                        />
                        Member Card Fee
                      </label>
                    </div>

                    <h5>Section 1: Personal Details</h5>
                    <div className="form-group">
                      <label>Full Names:</label>
                      <input
                        type="text"
                        name="fullNames"
                        value={editFormData.fullNames || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Other Names:</label>
                      <input
                        type="text"
                        name="otherNames"
                        value={editFormData.otherNames || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date of Birth:</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={editFormData.dateOfBirth || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Identity Document Number:</label>
                      <input
                        type="text"
                        name="identityDocumentNumber"
                        value={editFormData.identityDocumentNumber || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Gender:</label>
                      <select
                        name="gender"
                        value={editFormData.gender || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Nationality:</label>
                      <input
                        type="text"
                        name="nationality"
                        value={editFormData.nationality || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Current Marital Status:</label>
                      <select
                        name="currentMaritalStatus"
                        value={editFormData.currentMaritalStatus || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select Status</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Full Names of Spouse:</label>
                      <input
                        type="text"
                        name="fullNamesSpouse"
                        value={editFormData.fullNamesSpouse || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Previous Marital Status:</label>
                      <input
                        type="text"
                        name="previousMaritalStatus"
                        value={editFormData.previousMaritalStatus?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          previousMaritalStatus: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Full Names of Previous Spouse:</label>
                      <input
                        type="text"
                        name="fullNamesPreviousSpouse"
                        value={editFormData.fullNamesPreviousSpouse || ''}
                        onChange={handleEditChange}
                      />
                    </div>

                    <h5>Section 2: Contacts & Residence</h5>
                    <div className="form-group">
                      <label>Contact Numbers (Calls Main):</label>
                      <input
                        type="tel"
                        name="contactNumbersCallsMain"
                        value={editFormData.contactNumbersCallsMain || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Numbers (Calls Other):</label>
                      <input
                        type="tel"
                        name="contactNumbersCallsOther"
                        value={editFormData.contactNumbersCallsOther || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Numbers (WhatsApp Main):</label>
                      <input
                        type="tel"
                        name="contactNumbersWhatsappMain"
                        value={editFormData.contactNumbersWhatsappMain || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Numbers (WhatsApp Other):</label>
                      <input
                        type="tel"
                        name="contactNumbersWhatsappOther"
                        value={editFormData.contactNumbersWhatsappOther || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Personal Email Address:</label>
                      <input
                        type="email"
                        name="personalEmailAddress"
                        value={editFormData.personalEmailAddress || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Home Location/Village:</label>
                      <input
                        type="text"
                        name="homeLocationVillage"
                        value={editFormData.homeLocationVillage || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Street Full Description:</label>
                      <input
                        type="text"
                        name="streetFullDescription"
                        value={editFormData.streetFullDescription || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Name of Homeowner/Chief:</label>
                      <input
                        type="text"
                        name="nameHomeownerChief"
                        value={editFormData.nameHomeownerChief || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Social Media Channel Names:</label>
                      <input
                        type="text"
                        name="socialMediaChannelNames"
                        value={editFormData.socialMediaChannelNames || ''}
                        onChange={handleEditChange}
                      />
                    </div>

                    <h5>Section 3: Next of Kin Details</h5>
                    <div className="form-group">
                      <label>Title & Full Names:</label>
                      <input
                        type="text"
                        name="nextKinTitleFullNames"
                        value={editFormData.nextKinTitleFullNames || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Relationship:</label>
                      <input
                        type="text"
                        name="nextKinRelationship"
                        value={editFormData.nextKinRelationship || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Home Location/Village:</label>
                      <input
                        type="text"
                        name="nextKinHomeLocationVillage"
                        value={editFormData.nextKinHomeLocationVillage || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Number (Calls):</label>
                      <input
                        type="tel"
                        name="nextKinContactCalls"
                        value={editFormData.nextKinContactCalls || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Number (Text):</label>
                      <input
                        type="tel"
                        name="nextKinContactText"
                        value={editFormData.nextKinContactText || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Email Address:</label>
                      <input
                        type="email"
                        name="nextKinEmailAddress"
                        value={editFormData.nextKinEmailAddress || ''}
                        onChange={handleEditChange}
                      />
                    </div>

                    <h5>Section 4: Career & Employment</h5>
                    <h6>Current Employment</h6>
                    <div className="form-group">
                      <label>Employer Name:</label>
                      <input
                        type="text"
                        name="currentEmployerName"
                        value={editFormData.currentEmployerName || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Start Date - End Date:</label>
                      <input
                        type="text"
                        name="currentStartDateEndDate"
                        value={editFormData.currentStartDateEndDate || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Department:</label>
                      <input
                        type="text"
                        name="currentDepartment"
                        value={editFormData.currentDepartment || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Position:</label>
                      <input
                        type="text"
                        name="currentPosition"
                        value={editFormData.currentPosition || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Employment Number:</label>
                      <input
                        type="text"
                        name="currentEmploymentNumber"
                        value={editFormData.currentEmploymentNumber || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Number:</label>
                      <input
                        type="tel"
                        name="currentContactMain"
                        value={editFormData.currentContactMain || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Place/Location:</label>
                      <input
                        type="text"
                        name="currentPlaceLocation"
                        value={editFormData.currentPlaceLocation || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Industry/Sector:</label>
                      <input
                        type="text"
                        name="currentIndustrySector"
                        value={editFormData.currentIndustrySector || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <h6>Immediate Past Employment</h6>
                    <div className="form-group">
                      <label>Employer Name:</label>
                      <input
                        type="text"
                        name="immediatePastEmployerName"
                        value={editFormData.immediatePastEmployerName || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Start Date - End Date:</label>
                      <input
                        type="text"
                        name="immediatePastStartDateEndDate"
                        value={editFormData.immediatePastStartDateEndDate || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Department:</label>
                      <input
                        type="text"
                        name="immediatePastDepartment"
                        value={editFormData.immediatePastDepartment || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Position:</label>
                      <input
                        type="text"
                        name="immediatePastPosition"
                        value={editFormData.immediatePastPosition || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Employment Number:</label>
                      <input
                        type="text"
                        name="immediatePastEmploymentNumber"
                        value={editFormData.immediatePastEmploymentNumber || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Number:</label>
                      <input
                        type="tel"
                        name="immediatePastContactMain"
                        value={editFormData.immediatePastContactMain || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Place/Location:</label>
                      <input
                        type="text"
                        name="immediatePastPlaceLocation"
                        value={editFormData.immediatePastPlaceLocation || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Industry/Sector:</label>
                      <input
                        type="text"
                        name="immediatePastIndustrySector"
                        value={editFormData.immediatePastIndustrySector || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Highest Qualification:</label>
                      <input
                        type="text"
                        name="highestQualification"
                        value={editFormData.highestQualification || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Career Priorities:</label>
                      <input
                        type="text"
                        name="careerPriorities"
                        value={editFormData.careerPriorities?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          careerPriorities: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>FMLC Impact:</label>
                      <input
                        type="text"
                        name="fmlcImpact"
                        value={editFormData.fmlcImpact?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          fmlcImpact: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Other Impact:</label>
                      <input
                        type="text"
                        name="specifyOtherImpact"
                        value={editFormData.specifyOtherImpact || ''}
                        onChange={handleEditChange}
                      />
                    </div>

                    <h5>Section 5: Work, Life Schedules & Activities Style</h5>
                    <div className="form-group">
                      <label>Preferred Engagement Times:</label>
                      <input
                        type="text"
                        name="preferredEngagementTimes"
                        value={editFormData.preferredEngagementTimes?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          preferredEngagementTimes: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Best Activity Times:</label>
                      <input
                        type="text"
                        name="bestActivityTimes"
                        value={editFormData.bestActivityTimes?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          bestActivityTimes: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Preferred Activity Setup:</label>
                      <input
                        type="text"
                        name="preferredActivitySetup"
                        value={editFormData.preferredActivitySetup?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          preferredActivitySetup: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>

                    <h5>Section 6: Engagement with FMLC Brand</h5>
                    <div className="form-group">
                      <label>Motivating Incentives:</label>
                      <input
                        type="text"
                        name="motivatingIncentives"
                        value={editFormData.motivatingIncentives?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          motivatingIncentives: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>FMLC-Oriented Activities:</label>
                      <input
                        type="text"
                        name="fmlcOrientedActivities"
                        value={editFormData.fmlcOrientedActivities?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          fmlcOrientedActivities: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Refer Others:</label>
                      <input
                        type="text"
                        name="referOthers"
                        value={editFormData.referOthers || ''}
                        onChange={handleEditChange}
                      />
                    </div>

                    <h5>Section 7: Funeral Cover & Benefits</h5>
                    <h6>Children</h6>
                    {editFormData.children?.map((child, index) => (
                      <div key={index} className="form-group">
                        <h6>Child {index + 1}</h6>
                        <label>Name:</label>
                        <input
                          type="text"
                          name={`children.${index}.name`}
                          value={child.name || ''}
                          onChange={handleEditChange}
                        />
                        <label>Gender:</label>
                        <input
                          type="text"
                          name={`children.${index}.gender`}
                          value={child.gender || ''}
                          onChange={handleEditChange}
                        />
                        <label>Date of Birth:</label>
                        <input
                          type="date"
                          name={`children.${index}.dob`}
                          value={child.dob || ''}
                          onChange={handleEditChange}
                        />
                        <label>Issue Card:</label>
                        <input
                          type="text"
                          name={`children.${index}.issueCard`}
                          value={child.issueCard || ''}
                          onChange={handleEditChange}
                        />
                      </div>
                    ))}
                    <div className="form-group">
                      <label>Children Personalities:</label>
                      <input
                        type="text"
                        name="childrenPersonalities"
                        value={editFormData.childrenPersonalities?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          childrenPersonalities: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <h6>Parents/In-Laws</h6>
                    {editFormData.parentsInLaws?.map((parent, index) => (
                      <div key={index} className="form-group">
                        <h6>Parent/In-Law {index + 1}</h6>
                        <label>Name:</label>
                        <input
                          type="text"
                          name={`parentsInLaws.${index}.name`}
                          value={parent.name || ''}
                          onChange={handleEditChange}
                        />
                        <label>Gender:</label>
                        <input
                          type="text"
                          name={`parentsInLaws.${index}.gender`}
                          value={parent.gender || ''}
                          onChange={handleEditChange}
                        />
                        <label>Date of Birth:</label>
                        <input
                          type="date"
                          name={`parentsInLaws.${index}.dob`}
                          value={parent.dob || ''}
                          onChange={handleEditChange}
                        />
                        <label>Cover Type:</label>
                        <input
                          type="text"
                          name={`parentsInLaws.${index}.coverType`}
                          value={parent.coverType || ''}
                          onChange={handleEditChange}
                        />
                      </div>
                    ))}
                    <div className="form-group">
                      <label>Add Parents/In-Laws/Extended:</label>
                      <input
                        type="text"
                        name="addParentsInLawsExtended"
                        value={editFormData.addParentsInLawsExtended?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          addParentsInLawsExtended: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Parents Interests:</label>
                      <input
                        type="text"
                        name="parentsInterests"
                        value={editFormData.parentsInterests?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          parentsInterests: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>
                    <h6>Extended Family</h6>
                    {editFormData.extendedFamily?.map((member, index) => (
                      <div key={index} className="form-group">
                        <h6>Extended Family {index + 1}</h6>
                        <label>Name:</label>
                        <input
                          type="text"
                          name={`extendedFamily.${index}.name`}
                          value={member.name || ''}
                          onChange={handleEditChange}
                        />
                        <label>Gender:</label>
                        <input
                          type="text"
                          name={`extendedFamily.${index}.gender`}
                          value={member.gender || ''}
                          onChange={handleEditChange}
                        />
                        <label>Date of Birth:</label>
                        <input
                          type="date"
                          name={`extendedFamily.${index}.dob`}
                          value={member.dob || ''}
                          onChange={handleEditChange}
                        />
                        <label>Cover Type:</label>
                        <input
                          type="text"
                          name={`extendedFamily.${index}.coverType`}
                          value={member.coverType || ''}
                          onChange={handleEditChange}
                        />
                      </div>
                    ))}
                    <div className="form-group">
                      <label>Extended Family Motivations:</label>
                      <input
                        type="text"
                        name="externalFamilyMotivations"
                        value={editFormData.externalFamilyMotivations?.join(', ') || ''}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          externalFamilyMotivations: e.target.value.split(',').map(s => s.trim())
                        }))}
                      />
                    </div>

                    <h5>Section 8: Beneficiary Details</h5>
                    <div className="form-group">
                      <label>Title & Full Names:</label>
                      <input
                        type="text"
                        name="beneficiaryTitleFullNames"
                        value={editFormData.beneficiaryTitleFullNames || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>ID Number:</label>
                      <input
                        type="text"
                        name="beneficiaryIdNumber"
                        value={editFormData.beneficiaryIdNumber || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Nationality:</label>
                      <input
                        type="text"
                        name="beneficiaryNationality"
                        value={editFormData.beneficiaryNationality || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Relationship:</label>
                      <input
                        type="text"
                        name="beneficiaryRelationship"
                        value={editFormData.beneficiaryRelationship || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Home Location/Village:</label>
                      <input
                        type="text"
                        name="beneficiaryHomeLocationVillage"
                        value={editFormData.beneficiaryHomeLocationVillage || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Number (Calls):</label>
                      <input
                        type="tel"
                        name="beneficiaryContactCalls"
                        value={editFormData.beneficiaryContactCalls || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Number (Text):</label>
                      <input
                        type="tel"
                        name="beneficiaryContactText"
                        value={editFormData.beneficiaryContactText || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Email Address:</label>
                      <input
                        type="email"
                        name="beneficiaryEmailAddress"
                        value={editFormData.beneficiaryEmailAddress || ''}
                        onChange={handleEditChange}
                      />
                    </div>

                    <h5>Section 9: Member Application Declaration</h5>
                    <div className="form-section">
                      <h3>Section 9: Member Application Declaration</h3>
                      <p>By submitting this application, the undersigned acknowledges and agrees to the following terms regarding the Finite Magazine Lifestyle Club (FMLC) Membership Program:</p>
                      <ol>
                        <li>All applications are subject to review by the FMLC Advisory Team.</li>
                        <li>Final approval is based on alignment with FMLC values, business ethics, and market relevance.</li>
                        <li>Upon acceptance, the applicant will receive an official contract outlining roles, responsibilities, and benefit structures.</li>
                        <li>Signing the contract is mandatory to confirm Member status and activate privileges.</li>
                        <li>Approved Members will receive a personalized FMLC Card 7-14 working days after.</li>
                        <li>This card serves as formal identification and access credential within the FMLC ecosystem.</li>
                        <li>The Membership Card grants access to exclusive benefits, partner store privileges, and FMLC-hosted events.</li>
                        <li>Members must present their card during business interactions at affiliated locations and activations.</li>
                        <li>Business Partner stores reserve the right to verify card authenticity and identity prior to benefit redemption.</li>
                        <li>Misuse or unauthorized presentation of the card may result in suspension or termination of Membership.</li>
                        <li>The Club may offer personalized assistance, event support, or logistics coordination through its official Club Concierge Team. While some services may be included in Member privileges, the activation of Club Concierge services may incur additional costs.</li>
                      </ol>
                      <h4>General Terms & Conditions</h4>
                      <ol>
                        <li>All Members must uphold professional integrity and align their offerings with FMLC’s core themes.</li>
                        <li>Approval of applications takes 5–7 business days.</li>
                        <li>Membership does not guarantee exclusivity unless specified through a separate agreement.</li>
                        <li>Monthly administration fees are non-refundable once processed.</li>
                        <li>Events co-hosted with FMLC must reflect shared values and member benefit.</li>
                        <li>FMLC reserves the right to suspend membership for non-compliance or reputational harm.</li>
                        <li>Members must communicate any changes in personal and work status promptly.</li>
                        <li>Members may be featured in selected editorial or visual campaign materials unless otherwise indicated.</li>
                      </ol>

                      <h4>1. MEMBER (APPLICANT)</h4>
                      <div className="form-group">
                        <label>Title & Full Names</label>
                        <input
                          type="text"
                          name="memberTitleFullNames"
                          value={editFormData.memberTitleFullNames || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          name="memberDate"
                          value={editFormData.memberDate || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>

                      <h4>2. FMLC REPRESENTATIVE</h4>
                      <div className="form-group">
                        <label>Title & Full Names</label>
                        <input
                          type="text"
                          name="fmlcRepTitleFullNames"
                          value={editFormData.fmlcRepTitleFullNames || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Position</label>
                        <input
                          type="text"
                          name="fmlcRepPosition"
                          value={editFormData.fmlcRepPosition || ''}
                          onChange={handleEditChange}
                          required
                          readOnly
                        />
                      </div>
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          name="fmlcRepDate"
                          value={editFormData.fmlcRepDate || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Remarks</label>
                        <input
                          type="text"
                          name="fmlcRepRemarks"
                          value={editFormData.fmlcRepRemarks || ''}
                          onChange={handleEditChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Signature</label>
                        <input
                          type="text"
                          name="fmlcRepSignature"
                          value={editFormData.fmlcRepSignature || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>

                      <h4>3. FMLC APPROVER</h4>
                      <div className="form-group">
                        <label>Title & Full Names</label>
                        <input
                          type="text"
                          name="fmlcApprTitleFullNames"
                          value={editFormData.fmlcApprTitleFullNames || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Position</label>
                        <input
                          type="text"
                          name="fmlcApprPosition"
                          value={editFormData.fmlcApprPosition || ''}
                          onChange={handleEditChange}
                          required
                          readOnly
                        />
                      </div>
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          name="fmlcApprDate"
                          value={editFormData.fmlcApprDate || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Remarks</label>
                        <input
                          type="text"
                          name="fmlcApprRemarks"
                          value={editFormData.fmlcApprRemarks || ''}
                          onChange={handleEditChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Signature</label>
                        <input
                          type="text"
                          name="fmlcApprSignature"
                          value={editFormData.fmlcApprSignature || ''}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                    </div>

                    <h5>Membership Fee & Payment Details</h5>
                    <div className="form-group">
                      <label>Main Subscription Fee Quantity:</label>
                      <input
                        type="number"
                        name="mainSubscriptionFeeQty"
                        value={editFormData.mainSubscriptionFeeQty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Membership Card Quantity:</label>
                      <input
                        type="number"
                        name="membershipCardQty"
                        value={editFormData.membershipCardQty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Additional Membership Quantity:</label>
                      <input
                        type="number"
                        name="additionalMembershipQty"
                        value={editFormData.additionalMembershipQty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral Main & Spouse Quantity:</label>
                      <input
                        type="number"
                        name="funeralMainSpouseQty"
                        value={editFormData.funeralMainSpouseQty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral Children Quantity:</label>
                      <input
                        type="number"
                        name="funeralChildrenQty"
                        value={editFormData.funeralChildrenQty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral Parent 1 Quantity:</label>
                      <input
                        type="number"
                        name="funeralParent1Qty"
                        value={editFormData.funeralParent1Qty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral Parent 2 Quantity:</label>
                      <input
                        type="number"
                        name="funeralParent2Qty"
                        value={editFormData.funeralParent2Qty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral In-Law 1 Quantity:</label>
                      <input
                        type="number"
                        name="funeralInLaw1Qty"
                        value={editFormData.funeralInLaw1Qty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral In-Law 2 Quantity:</label>
                      <input
                        type="number"
                        name="funeralInLaw2Qty"
                        value={editFormData.funeralInLaw2Qty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral Extended (M55) Quantity:</label>
                      <input
                        type="number"
                        name="funeralExtended55Qty"
                        value={editFormData.funeralExtended55Qty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral Extended (M90) Quantity:</label>
                      <input
                        type="number"
                        name="funeralExtended90Qty"
                        value={editFormData.funeralExtended90Qty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Funeral Extended (M110) Quantity:</label>
                      <input
                        type="number"
                        name="funeralExtended110Qty"
                        value={editFormData.funeralExtended110Qty || ''}
                        onChange={handleEditChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Subscription Frequency:</label>
                      <select
                        name="subscriptionFrequency"
                        value={editFormData.subscriptionFrequency || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select Frequency</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annually">Annually</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          name="agreeToPay"
                          checked={editFormData.agreeToPay || false}
                          onChange={handleEditChange}
                        />
                        Agree to Pay
                      </label>
                    </div>
                    <div className="form-group">
                      <label>Payment Method:</label>
                      <select
                        name="paymentMethod"
                        value={editFormData.paymentMethod || ''}
                        onChange={handleEditChange}
                      >
                        <option value="">Select Payment Method</option>
                        <option value="cash">Cash</option>
                        <option value="bank">Bank Deposit/EFT</option>
                        <option value="mobile">Mobile Money Provider</option>
                      </select>
                    </div>
                    {editFormData.paymentMethod === 'mobile' && (
                      <>
                        <div className="form-group">
                          <label>M-Pesa Number:</label>
                          <input
                            type="tel"
                            name="mpesaNumber"
                            value={editFormData.mpesaNumber || ''}
                            onChange={handleEditChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Ecocash Number:</label>
                          <input
                            type="tel"
                            name="ecocashNumber"
                            value={editFormData.ecocashNumber || ''}
                            onChange={handleEditChange}
                          />
                        </div>
                      </>
                    )}
                    {editFormData.paymentMethod === 'bank' && (
                      <>
                        <div className="form-group">
                          <label>Account Holder:</label>
                          <input
                            type="text"
                            name="accountHolder"
                            value={editFormData.accountHolder || ''}
                            onChange={handleEditChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Bank Name:</label>
                          <input
                            type="text"
                            name="bankName"
                            value={editFormData.bankName || ''}
                            onChange={handleEditChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Account Number:</label>
                          <input
                            type="text"
                            name="accountNo"
                            value={editFormData.accountNo || ''}
                            onChange={handleEditChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Branch Code:</label>
                          <input
                            type="text"
                            name="branchCode"
                            value={editFormData.branchCode || ''}
                            onChange={handleEditChange}
                          />
                        </div>
                      </>
                    )}
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          name="agreeToTerms"
                          checked={editFormData.agreeToTerms || false}
                          onChange={handleEditChange}
                        />
                        Agree to Terms
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4>Application Type</h4>
                    <div className="detail-row">
                      <span className="detail-label">Application Type:</span>
                      <span className="detail-value">{selectedApplication.applicationType || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Membership Plan:</span>
                      <span className="detail-value">{selectedApplication.membershipPlan || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Member Card Fee:</span>
                      <span className="detail-value">{selectedApplication.memberCardFee ? 'Yes' : 'No'}</span>
                    </div>

                    <h4>Section 1: Personal Details</h4>
                    <div className="detail-row">
                      <span className="detail-label">Full Names:</span>
                      <span className="detail-value">{selectedApplication.fullNames || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Other Names:</span>
                      <span className="detail-value">{selectedApplication.otherNames || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Date of Birth:</span>
                      <span className="detail-value">{selectedApplication.dateOfBirth || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Identity Document Number:</span>
                      <span className="detail-value">{selectedApplication.identityDocumentNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Gender:</span>
                      <span className="detail-value">{selectedApplication.gender || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Nationality:</span>
                      <span className="detail-value">{selectedApplication.nationality || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Current Marital Status:</span>
                      <span className="detail-value">{selectedApplication.currentMaritalStatus || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Full Names of Spouse:</span>
                      <span className="detail-value">{selectedApplication.fullNamesSpouse || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Previous Marital Status:</span>
                      <span className="detail-value">{selectedApplication.previousMaritalStatus?.length > 0 ? selectedApplication.previousMaritalStatus.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Full Names of Previous Spouse:</span>
                      <span className="detail-value">{selectedApplication.fullNamesPreviousSpouse || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Personal ID File:</span>
                      <span className="detail-value">{selectedApplication.personalIdUrl ? <a href={selectedApplication.personalIdUrl} target="_blank" rel="noopener noreferrer">View File</a> : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Spouse ID File:</span>
                      <span className="detail-value">{selectedApplication.spouseIdUrl ? <a href={selectedApplication.spouseIdUrl} target="_blank" rel="noopener noreferrer">View File</a> : 'N/A'}</span>
                    </div>

                    <h4>Section 2: Contacts & Residence</h4>
                    <div className="detail-row">
                      <span className="detail-label">Contact Numbers (Calls Main):</span>
                      <span className="detail-value">{selectedApplication.contactNumbersCallsMain || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Numbers (Calls Other):</span>
                      <span className="detail-value">{selectedApplication.contactNumbersCallsOther || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Numbers (WhatsApp Main):</span>
                      <span className="detail-value">{selectedApplication.contactNumbersWhatsappMain || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Numbers (WhatsApp Other):</span>
                      <span className="detail-value">{selectedApplication.contactNumbersWhatsappOther || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Personal Email Address:</span>
                      <span className="detail-value">{selectedApplication.personalEmailAddress || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Home Location/Village:</span>
                      <span className="detail-value">{selectedApplication.homeLocationVillage || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Street Full Description:</span>
                      <span className="detail-value">{selectedApplication.streetFullDescription || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Name of Homeowner/Chief:</span>
                      <span className="detail-value">{selectedApplication.nameHomeownerChief || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Social Media Channel Names:</span>
                      <span className="detail-value">{selectedApplication.socialMediaChannelNames || 'N/A'}</span>
                    </div>

                    <h4>Section 3: Next of Kin Details</h4>
                    <div className="detail-row">
                      <span className="detail-label">Title & Full Names:</span>
                      <span className="detail-value">{selectedApplication.nextKinTitleFullNames || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Relationship:</span>
                      <span className="detail-value">{selectedApplication.nextKinRelationship || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Home Location/Village:</span>
                      <span className="detail-value">{selectedApplication.nextKinHomeLocationVillage || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Number (Calls):</span>
                      <span className="detail-value">{selectedApplication.nextKinContactCalls || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Number (Text):</span>
                      <span className="detail-value">{selectedApplication.nextKinContactText || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Email Address:</span>
                      <span className="detail-value">{selectedApplication.nextKinEmailAddress || 'N/A'}</span>
                    </div>

                    <h4>Section 4: Career & Employment</h4>
                    <h5>Current Employment</h5>
                    <div className="detail-row">
                      <span className="detail-label">Employer Name:</span>
                      <span className="detail-value">{selectedApplication.currentEmployerName || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Start Date - End Date:</span>
                      <span className="detail-value">{selectedApplication.currentStartDateEndDate || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Department:</span>
                      <span className="detail-value">{selectedApplication.currentDepartment || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Position:</span>
                      <span className="detail-value">{selectedApplication.currentPosition || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Employment Number:</span>
                      <span className="detail-value">{selectedApplication.currentEmploymentNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Number:</span>
                      <span className="detail-value">{selectedApplication.currentContactMain || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Place/Location:</span>
                      <span className="detail-value">{selectedApplication.currentPlaceLocation || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Industry/Sector:</span>
                      <span className="detail-value">{selectedApplication.currentIndustrySector || 'N/A'}</span>
                    </div>
                    <h5>Immediate Past Employment</h5>
                    <div className="detail-row">
                      <span className="detail-label">Employer Name:</span>
                      <span className="detail-value">{selectedApplication.immediatePastEmployerName || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Start Date - End Date:</span>
                      <span className="detail-value">{selectedApplication.immediatePastStartDateEndDate || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Department:</span>
                      <span className="detail-value">{selectedApplication.immediatePastDepartment || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Position:</span>
                      <span className="detail-value">{selectedApplication.immediatePastPosition || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Employment Number:</span>
                      <span className="detail-value">{selectedApplication.immediatePastEmploymentNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Number:</span>
                      <span className="detail-value">{selectedApplication.immediatePastContactMain || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Place/Location:</span>
                      <span className="detail-value">{selectedApplication.immediatePastPlaceLocation || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Industry/Sector:</span>
                      <span className="detail-value">{selectedApplication.immediatePastIndustrySector || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Highest Qualification:</span>
                      <span className="detail-value">{selectedApplication.highestQualification || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Career Priorities:</span>
                      <span className="detail-value">{selectedApplication.careerPriorities?.length > 0 ? selectedApplication.careerPriorities.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">FMLC Impact:</span>
                      <span className="detail-value">{selectedApplication.fmlcImpact?.length > 0 ? selectedApplication.fmlcImpact.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Other Impact:</span>
                      <span className="detail-value">{selectedApplication.specifyOtherImpact || 'N/A'}</span>
                    </div>

                    <h4>Section 5: Work, Life Schedules & Activities Style</h4>
                    <div className="detail-row">
                      <span className="detail-label">Preferred Engagement Times:</span>
                      <span className="detail-value">{selectedApplication.preferredEngagementTimes?.length > 0 ? selectedApplication.preferredEngagementTimes.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Best Activity Times:</span>
                      <span className="detail-value">{selectedApplication.bestActivityTimes?.length > 0 ? selectedApplication.bestActivityTimes.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Preferred Activity Setup:</span>
                      <span className="detail-value">{selectedApplication.preferredActivitySetup?.length > 0 ? selectedApplication.preferredActivitySetup.join(', ') : 'N/A'}</span>
                    </div>

                    <h4>Section 6: Engagement with FMLC Brand</h4>
                    <div className="detail-row">
                      <span className="detail-label">Motivating Incentives:</span>
                      <span className="detail-value">{selectedApplication.motivatingIncentives?.length > 0 ? selectedApplication.motivatingIncentives.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">FMLC-Oriented Activities:</span>
                      <span className="detail-value">{selectedApplication.fmlcOrientedActivities?.length > 0 ? selectedApplication.fmlcOrientedActivities.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Refer Others:</span>
                      <span className="detail-value">{selectedApplication.referOthers || 'N/A'}</span>
                    </div>

                    <h4>Section 7: Funeral Cover & Benefits</h4>
                    <h5>Children</h5>
                    {selectedApplication.children?.length > 0 ? (
                      selectedApplication.children.map((child, index) => (
                        <div key={index} className="detail-subsection">
                          <h6>Child {index + 1}</h6>
                          <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{child.name || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Gender:</span>
                            <span className="detail-value">{child.gender || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Date of Birth:</span>
                            <span className="detail-value">{child.dob || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Issue Card:</span>
                            <span className="detail-value">{child.issueCard || 'N/A'}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="detail-row">
                        <span className="detail-value">No children listed</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Children Personalities:</span>
                      <span className="detail-value">{selectedApplication.childrenPersonalities?.length > 0 ? selectedApplication.childrenPersonalities.join(', ') : 'N/A'}</span>
                    </div>
                    <h5>Parents/In-Laws</h5>
                    {selectedApplication.parentsInLaws?.length > 0 ? (
                      selectedApplication.parentsInLaws.map((parent, index) => (
                        <div key={index} className="detail-subsection">
                          <h6>Parent/In-Law {index + 1}</h6>
                          <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{parent.name || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Gender:</span>
                            <span className="detail-value">{parent.gender || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Date of Birth:</span>
                            <span className="detail-value">{parent.dob || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Cover Type:</span>
                            <span className="detail-value">{parent.coverType || 'N/A'}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="detail-row">
                        <span className="detail-value">No parents/in-laws listed</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Add Parents/In-Laws/Extended:</span>
                      <span className="detail-value">{selectedApplication.addParentsInLawsExtended?.length > 0 ? selectedApplication.addParentsInLawsExtended.join(', ') : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Parents Interests:</span>
                      <span className="detail-value">{selectedApplication.parentsInterests?.length > 0 ? selectedApplication.parentsInterests.join(', ') : 'N/A'}</span>
                    </div>
                    <h5>Extended Family</h5>
                    {selectedApplication.extendedFamily?.length > 0 ? (
                      selectedApplication.extendedFamily.map((member, index) => (
                        <div key={index} className="detail-subsection">
                          <h6>Extended Family {index + 1}</h6>
                          <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value">{member.name || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Gender:</span>
                            <span className="detail-value">{member.gender || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Date of Birth:</span>
                            <span className="detail-value">{member.dob || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Cover Type:</span>
                            <span className="detail-value">{member.coverType || 'N/A'}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="detail-row">
                        <span className="detail-value">No extended family listed</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Extended Family Motivations:</span>
                      <span className="detail-value">{selectedApplication.externalFamilyMotivations?.length > 0 ? selectedApplication.externalFamilyMotivations.join(', ') : 'N/A'}</span>
                    </div>

                    <h4>Section 8: Beneficiary Details</h4>
                    <div className="detail-row">
                      <span className="detail-label">Title & Full Names:</span>
                      <span className="detail-value">{selectedApplication.beneficiaryTitleFullNames || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">ID Number:</span>
                      <span className="detail-value">{selectedApplication.beneficiaryIdNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Nationality:</span>
                      <span className="detail-value">{selectedApplication.beneficiaryNationality || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Relationship:</span>
                      <span className="detail-value">{selectedApplication.beneficiaryRelationship || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Home Location/Village:</span>
                      <span className="detail-value">{selectedApplication.beneficiaryHomeLocationVillage || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Number (Calls):</span>
                      <span className="detail-value">{selectedApplication.beneficiaryContactCalls || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Number (Text):</span>
                      <span className="detail-value">{selectedApplication.beneficiaryContactText || 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Email Address:</span>
                      <span className="detail-value">{selectedApplication.beneficiaryEmailAddress || 'N/A'}</span>
                    </div>

                    <h4>Section 9: Member Application Declaration</h4>
                    <div className="form-section">
                      <h3>Section 9: Member Application Declaration</h3>
                      <p>By submitting this application, the undersigned acknowledges and agrees to the following terms regarding the Finite Magazine Lifestyle Club (FMLC) Membership Program:</p>
                      <ol>
                        <li>All applications are subject to review by the FMLC Advisory Team.</li>
                        <li>Final approval is based on alignment with FMLC values, business ethics, and market relevance.</li>
                        <li>Upon acceptance, the applicant will receive an official contract outlining roles, responsibilities, and benefit structures.</li>
                        <li>Signing the contract is mandatory to confirm Member status and activate privileges.</li>
                        <li>Approved Members will receive a personalized FMLC Card 7-14 working days after.</li>
                        <li>This card serves as formal identification and access credential within the FMLC ecosystem.</li>
                        <li>The Membership Card grants access to exclusive benefits, partner store privileges, and FMLC-hosted events.</li>
                        <li>Members must present their card during business interactions at affiliated locations and activations.</li>
                        <li>Business Partner stores reserve the right to verify card authenticity and identity prior to benefit redemption.</li>
                        <li>Misuse or unauthorized presentation of the card may result in suspension or termination of Membership.</li>
                        <li>The Club may offer personalized assistance, event support, or logistics coordination through its official Club Concierge Team. While some services may be included in Member privileges, the activation of Club Concierge services may incur additional costs.</li>
                      </ol>
                      <h4>General Terms & Conditions</h4>
                      <ol>
                        <li>All Members must uphold professional integrity and align their offerings with FMLC’s core themes.</li>
                        <li>Approval of applications takes 5–7 business days.</li>
                        <li>Membership does not guarantee exclusivity unless specified through a separate agreement.</li>
                        <li>Monthly administration fees are non-refundable once processed.</li>
                        <li>Events co-hosted with FMLC must reflect shared values and member benefit.</li>
                        <li>FMLC reserves the right to suspend membership for non-compliance or reputational harm.</li>
                        <li>Members must communicate any changes in personal and work status promptly.</li>
                        <li>Members may be featured in selected editorial or visual campaign materials unless otherwise indicated.</li>
                      </ol>

                      <h4>1. MEMBER (APPLICANT)</h4>
                      <div className="detail-row">
                        <span className="detail-label">Title & Full Names:</span>
                        <span className="detail-value">{selectedApplication.memberTitleFullNames || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Date:</span>
                        <span className="detail-value">{selectedApplication.memberDate || 'N/A'}</span>
                      </div>

                      <h4>2. FMLC REPRESENTATIVE</h4>
                      <div className="detail-row">
                        <span className="detail-label">Title & Full Names:</span>
                        <span className="detail-value">{selectedApplication.fmlcRepTitleFullNames || 'N/A'}</span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Position:</span>
                        <span className="detail-value">{selectedApplication.fmlcRepPosition || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Date:</span>
                        <span className="detail-value">{selectedApplication.fmlcRepDate || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Remarks:</span>
                        <span className="detail-value">{selectedApplication.fmlcRepRemarks || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Signature:</span>
                        <span className="detail-value">{selectedApplication.fmlcRepSignature || 'N/A'}</span>
                      </div>

                      <h4>3. FMLC APPROVER</h4>
                      <div className="detail-row">
                        <span className="detail-label">Title & Full Names:</span>
                        <span className="detail-value">{selectedApplication.fmlcApprTitleFullNames || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Position:</span>
                        <span className="detail-value">{selectedApplication.fmlcApprPosition || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Date:</span>
                        <span className="detail-value">{selectedApplication.fmlcApprDate || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Remarks:</span>
                        <span className="detail-value">{selectedApplication.fmlcApprRemarks || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Signature:</span>
                        <span className="detail-value">{selectedApplication.fmlcApprSignature || 'N/A'}</span>
                      </div>

                      <h4>Membership Fee & Payment Details</h4>
                      <div className="detail-row">
                        <span className="detail-label">Main Subscription Fee Quantity:</span>
                        <span className="detail-value">{selectedApplication.mainSubscriptionFeeQty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Membership Card Quantity:</span>
                        <span className="detail-value">{selectedApplication.membershipCardQty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Additional Membership Quantity:</span>
                        <span className="detail-value">{selectedApplication.additionalMembershipQty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral Main & Spouse Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralMainSpouseQty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral Children Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralChildrenQty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral Parent 1 Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralParent1Qty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral Parent 2 Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralParent2Qty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral In-Law 1 Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralInLaw1Qty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral In-Law 2 Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralInLaw2Qty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral Extended (M55) Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralExtended55Qty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral Extended (M90) Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralExtended90Qty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Funeral Extended (M110) Quantity:</span>
                        <span className="detail-value">{selectedApplication.funeralExtended110Qty || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Subscription Frequency:</span>
                        <span className="detail-value">{selectedApplication.subscriptionFrequency || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Agree to Pay:</span>
                        <span className="detail-value">{selectedApplication.agreeToPay ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Payment Method:</span>
                        <span className="detail-value">{selectedApplication.paymentMethod || 'N/A'}</span>
                      </div>
                      {selectedApplication.paymentMethod === 'mobile' && (
                        <>
                          <div className="detail-row">
                            <span className="detail-label">M-Pesa Number:</span>
                            <span className="detail-value">{selectedApplication.mpesaNumber || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Ecocash Number:</span>
                            <span className="detail-value">{selectedApplication.ecocashNumber || 'N/A'}</span>
                          </div>
                        </>
                      )}
                      {selectedApplication.paymentMethod === 'bank' && (
                        <>
                          <div className="detail-row">
                            <span className="detail-label">Account Holder:</span>
                            <span className="detail-value">{selectedApplication.accountHolder || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Bank Name:</span>
                            <span className="detail-value">{selectedApplication.bankName || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Account Number:</span>
                            <span className="detail-value">{selectedApplication.accountNo || 'N/A'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Branch Code:</span>
                            <span className="detail-value">{selectedApplication.branchCode || 'N/A'}</span>
                          </div>
                        </>
                      )}
                      <div className="detail-row">
                        <span className="detail-label">Agree to Terms:</span>
                        <span className="detail-value">{selectedApplication.agreeToTerms ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Coordinator Response Section */}
              <div className="coordinator-response">
                <h4>Coordinator Response</h4>
                {selectedApplication.coordinatorResponse ? (
                  <div className="response-details">
                    <p><strong>Response:</strong> {selectedApplication.coordinatorResponse}</p>
                    <p><strong>Timestamp:</strong> {selectedApplication.responseTimestamp ? new Date(selectedApplication.responseTimestamp.seconds * 1000).toLocaleString() : 'N/A'}</p>
                  </div>
                ) : (
                  <p>No response provided yet.</p>
                )}
                <textarea
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Enter your response or query..."
                  className="response-textarea"
                />
                <button 
                  onClick={handleRespondToQuery}
                  className="btn-primary"
                  disabled={!responseMessage.trim()}
                >
                  Send Response
                </button>
              </div>
            </div>

            <div className="modal-footer">
              {editMode ? (
                <>
                  <button 
                    onClick={saveEditedForm}
                    className="btn-primary"
                  >
                    Save Changes
                  </button>
                  <button 
                    onClick={() => setEditMode(false)}
                    className="btn-secondary"
                  >
                    Cancel Edit
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => setEditMode(true)}
                  className="btn-primary"
                >
                  Edit Application
                </button>
              )}
              <button 
                onClick={closeModal}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberCoordinator;